// Team details interface

import { useState, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFirebase } from './FirebaseContextProvider';
import CardTemplate from './CardTemplate';
import TournamentsList from './TournamentsList';
import Tabs from './Tabs';
import PlayersList from './PlayersList';
import PropItem from './PropItem';
import PropItemsBlock from './PropItemsBlock';
import TeamCardEdit from './TeamCardEdit';
import MultiSelectInline from './MultiSelectInline';
import { UsersRoles } from '../utils/enums';
import { teamUpdate, userUpdate } from '../utils/firebase';
import { useOrgUsers } from '../utils/hooks';
import { switchGender, toOptions } from '../utils/misc';
import { groupByLabel, AgeGroupTypes } from '../utils/agegroups';
import GuidedTour from './GuidedTour';
import InviteButton from './InviteButton';
import placeholderTeam from '../assets/images/placeholder-team.png';
import './TeamCard.scss';

const stepsCoach = [
  {
    target: '.tabs-header-item:nth-of-type(1)',
    title: 'Players',
    content: 'That tab shows your team roster',
  },
  {
    target: '.tabs-header-item:nth-of-type(2)',
    title: 'Tournaments',
    content: 'This tab shows tournaments available for your team',
  },
];

const stepsTournaments = [
  {
    target: '.tournament:first-of-type',
    title: 'Tournament',
    content:
      'This tournament is available for your team. Click to see the details',
  },
  {
    target: '.tournament:first-of-type .col-7',
    title: 'Booking',
    content:
      "View booking and payment info here. Click 'Pay' to book the tournament",
  },
];

const stepsPlayer = [
  {
    target: '.tabs-header-item:nth-of-type(1)',
    title: 'Players',
    content: 'That tab shows your teammates roster',
  },
  {
    target: '.tabs-header-item:nth-of-type(2)',
    title: 'Tournaments',
    content: 'This tab shows tournaments available for your team',
  },
];

export default function TeamCard({ team }) {
  const [showSelectCoaches, setShowSelectCoaches] = useState(false);
  const manageCoachesBtnRef = useRef(null);
  const {
    user,
    tournaments,
    userIsAdmin,
    userIsCoach,
    userIsPlayer,
    coachesByTeamId,
  } = useFirebase();

  // Only an admin can open the picker below, and it is the only thing on this
  // card that needs the organization's coaches — so nobody else sends a query
  const { users: orgCoaches, isLoaded: areOrgCoachesLoaded } = useOrgUsers(
    userIsAdmin ? UsersRoles.COACH : null,
  );
  const orgCoachesById = useMemo(
    () => new Map(orgCoaches.map((coach) => [coach.id, coach])),
    [orgCoaches],
  );

  const {
    id,
    imgUrl,
    imgThumbUrl,
    name,
    location,
    age,
    gender,
    sport,
    cutOffDate,
  } = team;

  const userIsTheCoach = user.teams?.includes(id);

  // Tabs are hash-driven and only the active one is mounted, so the tour steps
  // follow the open tab
  const { hash } = useLocation();
  const onTournamentsTab = hash === '#tournaments';

  // A coach with no name yet renders as an empty link with a stray comma
  const namedCoaches = (coachesByTeamId.get(id) || []).filter((c) => c.name);

  // The team document mirrors its coaches through a trigger and lags a write by
  // seconds. This picker edits `users.teams`, so it reads that field back —
  // diffing against the mirror inside the window duplicates a team id
  const coachIdsKey = useMemo(
    () =>
      orgCoaches
        .filter((u) => u.teams?.includes(id))
        .map((u) => u.id)
        .sort()
        .join(','),
    [orgCoaches, id],
  );

  // Keyed on content, not on the `orgCoaches` identity (P7): the picker resets
  // its checkboxes whenever `value` is a new array, and any coach changing
  // would otherwise wipe a selection the admin is still making
  const editableCoachIds = useMemo(
    () => (coachIdsKey ? coachIdsKey.split(',') : []),
    [coachIdsKey],
  );

  const handleSelectCoaches = async (newCoachIds) => {
    // Determine which coaches need to be added and which need to be removed
    const coachesToAdd = newCoachIds.filter(
      (coachId) => !editableCoachIds.includes(coachId),
    );
    const coachesToRemove = editableCoachIds.filter(
      (coachId) => !newCoachIds.includes(coachId),
    );

    // Update all affected coaches
    // Warning: parallel updates with Promise.all may cause race conditions between local state and the database
    for (const coachId of coachesToAdd) {
      const coach = orgCoachesById.get(coachId);
      if (!coach) continue;
      // Deduped at the write: the diff above is only as fresh as the last
      // snapshot, and a second Ok inside that window would append twice
      const updatedTeams = [...new Set([...(coach.teams || []), id])];
      await userUpdate({ id: coachId, teams: updatedTeams });
    }
    for (const coachId of coachesToRemove) {
      const coach = orgCoachesById.get(coachId);
      if (!coach) continue;
      const updatedTeams = (coach.teams || []).filter(
        (teamId) => teamId !== id,
      );
      await userUpdate({ id: coachId, teams: updatedTeams });
    }

    setShowSelectCoaches(false);
  };

  return (
    <GuidedTour
      steps={
        onTournamentsTab
          ? userIsCoach
            ? stepsTournaments
            : []
          : userIsCoach && name
            ? stepsCoach
            : userIsPlayer
              ? stepsPlayer
              : []
      }
      // Wait for the tournaments to load, otherwise the steps find no row
      run={!onTournamentsTab || tournaments.length > 0}
      tourKey={onTournamentsTab ? 'tournaments-list-v1' : 'team-card-v1'}
    >
      <CardTemplate
        header='Team'
        className='team-card'
        backBtn
        imgUrl={imgUrl}
        imgThumbUrl={imgThumbUrl}
        imgTitle={name}
        name={name}
        placeholder={placeholderTeam}
        details={
          <PropItemsBlock>
            <PropItem prop='Location' value={location} />
            <PropItem prop='Sport' value={sport} />
            <PropItem
              prop='Division'
              value={`${switchGender(gender)}, ${age}${
                groupByLabel(age)?.type === AgeGroupTypes.U
                  ? ` (${cutOffDate})`
                  : ''
              }`}
            />
            <div className='coach-wrap'>
              <p className='gray-600 font-medium'>Coach: </p>
              <div className='coach-list'>
                {namedCoaches.map((coach, i, coaches) => (
                  <div className='coach-item' key={coach.id}>
                    <Link
                      to={'/coaches/' + coach.id}
                      className='user-link upper gray-1000'
                    >
                      {coach.name}
                      {i < coaches.length - 1 && ','}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
            {userIsAdmin && (
              <div className='buttons'>
                <div className='manage-coaches'>
                  {/* The roster arrives with the card now, not with the
                      session: opening the picker before it lands would show an
                      empty list with nothing checked */}
                  <button
                    className='small bw'
                    disabled={!areOrgCoachesLoaded}
                    onClick={() => setShowSelectCoaches(true)}
                    ref={manageCoachesBtnRef}
                  >
                    Manage coaches
                  </button>
                  <MultiSelectInline
                    className='select-coaches'
                    header='Choose coaches'
                    search
                    options={toOptions(orgCoaches)}
                    value={editableCoachIds}
                    small
                    show={showSelectCoaches}
                    onChange={handleSelectCoaches}
                    onClose={() => setShowSelectCoaches(false)}
                    anchorRef={manageCoachesBtnRef}
                  />
                </div>
                <InviteButton
                  tokenCreatePayload={{ team: id, role: UsersRoles.COACH }}
                  invited='coach'
                >
                  Invite coach
                </InviteButton>
              </div>
            )}
          </PropItemsBlock>
        }
        onDelete={
          userIsAdmin && (async () => await teamUpdate({ id, active: false }))
        }
        afterDeleteNavPath='/admin#teams'
        editForm={
          (userIsAdmin ||
            userIsTheCoach ||
            (userIsCoach && team.id === 'new')) && <TeamCardEdit team={team} />
        }
      >
        <Tabs
          className='team-card-tabs'
          tabs={[
            {
              title: 'Players',
              hash: '#players',
              component: <PlayersList teamId={id} />,
            },
            {
              title: 'Tournaments',
              hash: '#tournaments',
              component: <TournamentsList teamId={id} />,
            },
          ]}
        />
      </CardTemplate>
    </GuidedTour>
  );
}
